import React from "react";
import { Star } from "lucide-react";

/**
 * StarRating — read-only or interactive.
 * Props:
 *   value:    number (0..5, fractional allowed for display)
 *   onChange: function(rating: number)  — if provided, becomes interactive
 *   size:     icon size in px (default 14)
 *   showValue: also render numeric "(4.7)" next to stars
 *   reviewCount: optional count to render "(123)"
 */
const StarRating = ({
  value = 0,
  onChange,
  size = 14,
  showValue = false,
  reviewCount,
  className = "",
  testIdPrefix = "star",
}) => {
  const v = Number.isFinite(value) ? value : 0;
  const interactive = typeof onChange === "function";
  const stars = [1, 2, 3, 4, 5].map((n) => {
    const filled = v >= n - 0.25;
    const half = !filled && v >= n - 0.75;
    return (
      <button
        key={n}
        type="button"
        disabled={!interactive}
        onClick={() => interactive && onChange(n)}
        data-testid={`${testIdPrefix}-${n}`}
        aria-label={`${n} star${n > 1 ? "s" : ""}`}
        className={`${interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"} relative inline-flex`}
      >
        <Star
          width={size}
          height={size}
          className={filled ? "fill-[#D9923B] text-[#D9923B]" : "text-[#D9923B]/40"}
        />
        {half && (
          <Star
            width={size}
            height={size}
            className="fill-[#D9923B] text-[#D9923B] absolute left-0 top-0"
            style={{ clipPath: "inset(0 50% 0 0)" }}
          />
        )}
      </button>
    );
  });

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <span className="inline-flex gap-0.5">{stars}</span>
      {showValue && v > 0 && (
        <span className="text-xs font-semibold text-foreground/80">{v.toFixed(1)}</span>
      )}
      {typeof reviewCount === "number" && reviewCount > 0 && (
        <span className="text-xs text-muted-foreground">({reviewCount})</span>
      )}
      {typeof reviewCount === "number" && reviewCount === 0 && !showValue && (
        <span className="text-xs text-muted-foreground">No reviews yet</span>
      )}
    </span>
  );
};

export default StarRating;
